/**
 * PermissionGuard - Component for conditionally rendering UI based on user permissions
 * 
 * Provides permission-based access control for UI elements including:
 * - Single permission checks
 * - Multiple permission checks (any/all)
 * - Role-based checks
 * - Fallback rendering for unauthorized users
 * 
 * Requirements: 7.2, 7.3 - Frontend displays only menu items and actions user has permission to access
 */
import React from 'react';
import { useAuth, Permission, UserRole } from '../contexts/AuthContext';

/**
 * PermissionGuard - Conditionally renders children based on user permissions
 * 
 * @param {Object} props
 * @param {string} props.permission - Single permission to check
 * @param {string[]} props.permissions - Array of permissions to check
 * @param {string} props.mode - 'any' (default) or 'all' for multiple permissions
 * @param {string} props.role - Single role to check
 * @param {string[]} props.roles - Array of roles to check (any match grants access)
 * @param {React.ReactNode} props.children - Content to render if authorized
 * @param {React.ReactNode} props.fallback - Content to render if unauthorized (optional)
 * @param {boolean} props.disabled - If true, renders children but disabled (for buttons)
 * @param {Function} props.renderDisabled - Custom render function for disabled state
 * 
 * Usage examples:
 * 
 * // Single permission check
 * <PermissionGuard permission={Permission.VIEW_EMPLOYEES}>
 *   <EmployeeList />
 * </PermissionGuard>
 * 
 * // Multiple permissions - any match
 * <PermissionGuard permissions={[Permission.VIEW_EMPLOYEES, Permission.VIEW_REPORTS]} mode="any">
 *   <Dashboard />
 * </PermissionGuard>
 * 
 * // Multiple permissions - all required
 * <PermissionGuard permissions={[Permission.VIEW_EMPLOYEES, Permission.EDIT_EMPLOYEES]} mode="all">
 *   <EmployeeEditor />
 * </PermissionGuard>
 * 
 * // Role-based check
 * <PermissionGuard role={UserRole.HR_ADMIN}>
 *   <AdminPanel />
 * </PermissionGuard>
 * 
 * // With fallback
 * <PermissionGuard permission={Permission.MANAGE_SYSTEM} fallback={<AccessDenied />}>
 *   <SystemSettings />
 * </PermissionGuard>
 * 
 * // Disabled mode (renders but disabled)
 * <PermissionGuard permission={Permission.EDIT_EMPLOYEES} disabled>
 *   <Button>Edit Employee</Button>
 * </PermissionGuard>
 */
export function PermissionGuard({
  permission,
  permissions,
  mode = 'any',
  role,
  roles,
  children,
  fallback = null,
  disabled = false,
  renderDisabled,
}) {
  const { hasPermission, hasAnyPermission, hasAllPermissions, hasRole, isLoading } = useAuth();

  // While loading auth state, don't render anything or render fallback
  if (isLoading) {
    return fallback;
  }

  // Determine if user is authorized
  let isAuthorized = false;

  // Check single permission
  if (permission) {
    isAuthorized = hasPermission(permission);
  }
  // Check multiple permissions
  else if (permissions && permissions.length > 0) {
    if (mode === 'all') {
      isAuthorized = hasAllPermissions(permissions);
    } else {
      isAuthorized = hasAnyPermission(permissions);
    }
  }
  // Check single role
  else if (role) {
    isAuthorized = hasRole(role);
  }
  // Check multiple roles (any match)
  else if (roles && roles.length > 0) {
    isAuthorized = roles.some((r) => hasRole(r));
  }
  // No permission/role specified - allow access
  else {
    isAuthorized = true;
  }

  // If authorized, render children
  if (isAuthorized) {
    return <>{children}</>;
  }

  // If disabled mode, render children with disabled state
  if (disabled) {
    if (renderDisabled) {
      return renderDisabled(children);
    }
    // Clone children and add disabled prop
    return (
      <>
        {React.Children.map(children, (child) => {
          if (React.isValidElement(child)) {
            return React.cloneElement(child, {
              disabled: true,
              'aria-disabled': true,
              className: `${child.props.className || ''} opacity-50 cursor-not-allowed`.trim(),
            });
          }
          return child;
        })}
      </>
    );
  }

  // Otherwise render fallback
  return fallback;
}

/**
 * RequirePermission - Higher-order component for permission-based access
 * 
 * @param {string|string[]} permissionOrPermissions - Permission(s) to check
 * @param {Object} options - Options for the guard
 * @returns {Function} HOC that wraps component with permission check
 * 
 * Usage:
 * const ProtectedComponent = RequirePermission(Permission.VIEW_EMPLOYEES)(MyComponent);
 */
export function RequirePermission(permissionOrPermissions, options = {}) {
  return function withPermission(WrappedComponent) {
    return function PermissionWrapper(props) {
      const guardProps = Array.isArray(permissionOrPermissions)
        ? { permissions: permissionOrPermissions, ...options }
        : { permission: permissionOrPermissions, ...options };

      return (
        <PermissionGuard {...guardProps}>
          <WrappedComponent {...props} />
        </PermissionGuard>
      );
    };
  };
}

/**
 * RequireRole - Higher-order component for role-based access
 * 
 * @param {string|string[]} roleOrRoles - Role(s) to check
 * @param {Object} options - Options for the guard
 * @returns {Function} HOC that wraps component with role check
 * 
 * Usage:
 * const AdminComponent = RequireRole(UserRole.HR_ADMIN)(MyComponent);
 */
export function RequireRole(roleOrRoles, options = {}) {
  return function withRole(WrappedComponent) {
    return function RoleWrapper(props) {
      const guardProps = Array.isArray(roleOrRoles)
        ? { roles: roleOrRoles, ...options }
        : { role: roleOrRoles, ...options };

      return (
        <PermissionGuard {...guardProps}>
          <WrappedComponent {...props} />
        </PermissionGuard>
      );
    };
  };
}

/**
 * usePermissionCheck - Hook for programmatic permission checks
 * 
 * @param {string} permission - Permission to check
 * @returns {boolean} Whether user has the permission
 * 
 * Usage:
 * const canEdit = usePermissionCheck(Permission.EDIT_EMPLOYEES);
 * if (canEdit) { ... }
 */
export function usePermissionCheck(permission) {
  const { hasPermission } = useAuth();
  return hasPermission(permission);
}

/**
 * useRoleCheck - Hook for programmatic role checks
 * 
 * @param {string} role - Role to check
 * @returns {boolean} Whether user has the role
 * 
 * Usage:
 * const isAdmin = useRoleCheck(UserRole.HR_ADMIN);
 * if (isAdmin) { ... }
 */
export function useRoleCheck(role) {
  const { hasRole } = useAuth();
  return hasRole(role);
}

// Re-export Permission and UserRole for convenience
export { Permission, UserRole };

export default PermissionGuard;
